import * as yup from "yup";
import { useFormik } from "formik";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import { FormArea, FeildWraper, SelectSearch, FeildError, } from "../../../../component/form/form-layout";
import { Table, TableBody, TableCard, TableCell, TableHead, TableRow } from "../../../../component/table/table-layout";
import { Copy, Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import moment from "moment";

const AddItemPopup = ({
    onClose = () => { },
    onError = () => { },
    onSubmit = () => { },
    currentData,
    dropdowns,
    Style,
}) => {
    const apiCall = useApiCall();
    const [loadingItems, setLoadingItems] = useState(false);

    /* ===================== VALIDATION ===================== */
    const validationSchema = yup.object({

        purchases: yup
            .array()
            .of(
                yup.object({
                    item_id: yup.number().required("Item required"),
                    variant_id: yup.number().nullable(),
                    quantity: yup
                        .number()
                        .required("Qty required")
                        .positive("Qty > 0")
                        .integer("Must be integer"),
                    amount: yup
                        .number()
                        .required("Amount required")
                        .positive("Amount > 0"),
                    date: yup.string().required("Date Required"),
                    vendor_id: yup.string().required("Vendor Required"),
                    bill_receipt_number: yup.string().required("Receipt Required"),
                    location_id: yup.string().required("Building Required"),
                    note: yup.string().nullable(),
                })
            )
            .min(1, "At least one item is required"),
    });

    /* ===================== FORMIK ===================== */
    const formik = useFormik({
        initialValues: {
            purchases: [
                { item_id: "", variant_id: "", quantity: 1, amount: "", note: "", date: "", vendor_id: "", bill_receipt_number: "", location_id: "" },
            ],
        },
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: async (values) => {
            const payload = {
                purchases: values.purchases.map(({ amount, note, ...rest }) => ({
                    ...rest,
                    total_amount: Number(amount),
                    notes: note,
                })),
            };

            try {
                const res = await apiCall({
                    method: "post",
                    url: "store-purchase-multiple",
                    data: JSON.stringify(payload),
                });
                onSubmit(res?.message || "Success");
                formik.resetForm();
            } catch (err) {
                onError(
                    err?.response?.data?.message || "Something went wrong"
                );
            }
        },
    });

    console.log("currentData", currentData);

    /* ===================== EDIT MODE ===================== */
    useEffect(() => {
        if (!currentData?.id) return;

        const loadEditData = async () => {
            setLoadingItems(true);
            try {
                const res = await apiCall({ method: "get", url: `get-purchase-for-edit?purchase_id=${currentData.id}` });
                const items = res?.data?.purchases || [];

                formik.setValues({
                    invoice_date: currentData?.date
                        ? moment(currentData.date).format("YYYY-MM-DD")
                        : "",
                    bill_receipt_number: currentData?.bill_receipt_number ?? "",
                    vendor_id: currentData?.vendor_id ?? "",
                    total_amount: currentData?.total_amount?.replaceAll("PKR ", "")?.replaceAll(",", "") ?? "",
                    location_id: currentData?.location_id ?? "",
                    notes: currentData?.notes ?? "",
                    invoice_path: currentData?.invoice_path ?? "",
                    purchases: items.map(({ total_amount, notes, ...rest }) => ({
                        ...rest,
                        amount: Number(total_amount),
                        note: notes,
                    })),
                });
            } finally {
                setLoadingItems(false);
            }
        };

        loadEditData();
    }, [currentData]);

    /* ===================== ROW HANDLERS ===================== */
    const addRow = () =>
        formik.setFieldValue("purchases", [
            ...formik.values.purchases,
            { item_id: "", variant_id: "", quantity: 1, amount: "", note: "" },
        ]);

    const removeRow = (idx) => {
        const rows = [...formik.values.purchases];
        rows.splice(idx, 1);
        formik.setFieldValue("purchases", rows);
    };

    const copyRow = (idx) =>
        formik.setFieldValue("purchases", [
            ...formik.values.purchases,
            formik.values.purchases[idx],
        ]);

    /* ===================== UI ===================== */
    return (
        <form onSubmit={formik.handleSubmit} className={Style.modal_main}>
            <div className={Style.modal_body}>
                <FormArea>
                    {/* TABLE */}
                    <FeildWraper width="100%">
                        {loadingItems && <p>Loading items…</p>}
                        <TableCard>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell header sticky />
                                        <TableCell header>Date</TableCell>
                                        <TableCell header>Item</TableCell>
                                        <TableCell header>Variant</TableCell>
                                        <TableCell header>Qty</TableCell>
                                        <TableCell header>Amount</TableCell>
                                        <TableCell header>Vendor</TableCell>
                                        <TableCell header>Receipt</TableCell>
                                        <TableCell header>Building</TableCell>
                                        <TableCell header>Note</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {formik.values.purchases.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell sticky className={'!w-fit min-w-0 !px-1'}>
                                                <div className="flex items-center gap-0.5">
                                                    {/* Add Row */}
                                                    <button
                                                        type="button"
                                                        onClick={addRow}
                                                        className="flex h-4 w-4 items-center justify-center rounded-sm border border-gray-300 bg-white text-gray-600hover:bg-green-50 hover:text-green-600 hover:border-green-400 transition-all duration-200"
                                                    >
                                                        <Plus size={10} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => copyRow(idx)}
                                                        className="flex h-4 w-4 items-center justify-center rounded-sm border border-gray-300 bg-white text-gray-600hover:bg-green-50 hover:text-green-600 hover:border-green-400 transition-all duration-200"
                                                    >
                                                        <Copy size={8} />
                                                    </button>

                                                    {/* Remove Row */}
                                                    {formik.values.purchases.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeRow(idx)}
                                                            className="flex h-4 w-4 items-center justify-center rounded-sm border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-400 transition-all duration-200"
                                                        >
                                                            <Minus size={10} />
                                                        </button>
                                                    )}
                                                </div>
                                            </TableCell>

                                            <TableCell>
                                                <input
                                                    className={'!w-[165px]'}
                                                    type="date"
                                                    value={row.date}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`purchases.${idx}.date`, e.target.value)
                                                    }
                                                />
                                                <FeildError style={{ position: "relative" }}>{formik.errors.purchases?.[idx]?.date}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <SelectSearch
                                                    menuPosition="fixed"
                                                    className={'!w-[165px]'}
                                                    value={row.item_id}
                                                    onChange={(v) => formik.setFieldValue(`purchases.${idx}.item_id`, v)}
                                                    options={dropdowns?.items?.map(i => ({
                                                        label: i.name,
                                                        value: i.id,
                                                    }))}
                                                />
                                                <FeildError style={{ position: "relative" }}>{formik.errors.purchases?.[idx]?.item_id}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <SelectSearch
                                                    menuPosition="fixed"
                                                    className={'!w-[165px]'}
                                                    value={row.variant_id}
                                                    onChange={(v) => formik.setFieldValue(`purchases.${idx}.variant_id`, v)}
                                                    options={dropdowns?.variants?.map(v => ({
                                                        label: v.name,
                                                        value: v.id,
                                                    }))}
                                                />
                                            </TableCell>

                                            <TableCell>
                                                <input
                                                    className={'!w-[120px]'}
                                                    type="number"
                                                    value={row.quantity}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`purchases.${idx}.quantity`, e.target.value)
                                                    }
                                                />
                                                <FeildError style={{ position: "relative" }}>{formik.errors.purchases?.[idx]?.quantity}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <input
                                                    className={'!w-[120px]'}
                                                    type="number"
                                                    value={row.amount}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`purchases.${idx}.amount`, e.target.value)
                                                    }
                                                />
                                                <FeildError style={{ position: "relative" }}>{formik.errors.purchases?.[idx]?.amount}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <SelectSearch
                                                    menuPosition="fixed"
                                                    className={'!w-[165px]'}
                                                    value={row.vendor_id}
                                                    onChange={(v) => formik.setFieldValue(`purchases.${idx}.vendor_id`, v)}
                                                    options={dropdowns?.vendor?.map(v => ({
                                                        label: v.full_name,
                                                        value: v.id,
                                                    }))}
                                                />
                                                <FeildError style={{ position: "relative" }}>{formik.errors.purchases?.[idx]?.vendor_id}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <input
                                                    className={'!w-[165px]'}
                                                    type="text"
                                                    value={row.bill_receipt_number}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`purchases.${idx}.bill_receipt_number`, e.target.value)
                                                    }
                                                />
                                                <FeildError style={{ position: "relative" }}>{formik.errors.purchases?.[idx]?.bill_receipt_number}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <SelectSearch
                                                    menuPosition="fixed"
                                                    className={'!w-[165px]'}
                                                    value={row.location_id}
                                                    onChange={(v) => formik.setFieldValue(`purchases.${idx}.location_id`, v)}
                                                    options={dropdowns?.buildings?.map(b => ({
                                                        label: b.name,
                                                        value: b.id,
                                                    }))}
                                                />
                                                <FeildError style={{ position: "relative" }}>{formik.errors.purchases?.[idx]?.location_id}</FeildError>
                                            </TableCell>

                                            <TableCell>
                                                <input
                                                    className={'!w-[165px]'}
                                                    value={row.note}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`purchases.${idx}.note`, e.target.value)
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableCard>
                    </FeildWraper>

                </FormArea>
            </div>

            <div className={Style.modal_foot}>
                <button type='reset' className={`${Style.dcmb__cancel}`} onClick={() => onClose()}>Discard</button>
                <button type='submit' className={`${Style.dcmb__submit}`}>Apply</button>
            </div>
        </form>
    );
};

export default AddItemPopup;